/**
 * Voters — polling-unit code shape.
 *
 * An INEC polling-unit code is four segments, state-LGA-ward-unit, zero-padded to 2-2-2-3:
 *
 *     04-09-02-011
 *
 * People paste it in every shape a spreadsheet or a phone call produces: `4-9-2-11`, `04/09/02/011`,
 * `04 09 02 011`, `04.09.02.011`, or the nine digits run together. This module turns all of those
 * into the one canonical form the server validates (`^\d{2}-\d{2}-\d{2}-\d{3}$`), or says exactly
 * which segment is wrong. Pure — no React, no network — so every shape is pinned by a test.
 */

/** Width of each segment, state → unit. */
export const PU_SEGMENT_WIDTHS = [2, 2, 2, 3] as const;

export const PU_CODE_PATTERN = /^\d{2}-\d{2}-\d{2}-\d{3}$/;

export type PuLevel = 'state' | 'lga' | 'ward' | 'pu';

export const PU_LEVELS: readonly PuLevel[] = ['state', 'lga', 'ward', 'pu'];

const LEVEL_NAMES: Record<PuLevel, string> = { state: 'state', lga: 'LGA', ward: 'ward', pu: 'unit' };

export interface IPuCodeParse {
	ok: boolean;
	/** Canonical `SS-LL-WW-PPP` when ok; '' otherwise. */
	code: string;
	/** The padded segments that could be read, in order. May be shorter than four when !ok. */
	segments: string[];
	/** One sentence, present when !ok. */
	reason?: string;
}

const pad = (segment: string, width: number): string | null => {
	const digits = segment.replace(/^0+(?=\d)/, '');
	if (!/^\d+$/.test(digits)) return null;
	if (digits.length > width) return null;
	return digits.padStart(width, '0');
};

/** Split on any run of separators people actually use. */
const splitRaw = (raw: string): string[] => raw.split(/[\s\-/.\\_|,]+/).filter(Boolean);

/**
 * Read up to `depth` segments of a code — for the address ladder, which accepts a partial paste
 * (`04-09` means "Onitsha North, pick the ward yourself").
 */
export const parsePartialPuCode = (raw: unknown, depth = 4): IPuCodeParse => {
	const value = typeof raw === 'number' ? String(raw) : typeof raw === 'string' ? raw.trim() : '';
	if (!value) return { ok: false, code: '', segments: [], reason: 'Enter a polling-unit code.' };

	let parts = splitRaw(value);

	// Nine digits run together — the only unambiguous unseparated form.
	if (parts.length === 1 && /^\d{9}$/.test(parts[0])) {
		const d = parts[0];
		parts = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 6), d.slice(6)];
	}

	if (parts.length > depth) {
		return { ok: false, code: '', segments: [], reason: `A polling-unit code has ${depth} parts — this has ${parts.length}.` };
	}

	const segments: string[] = [];
	for (let i = 0; i < parts.length; i++) {
		const padded = pad(parts[i], PU_SEGMENT_WIDTHS[i]);
		if (padded === null) {
			return {
				ok: false,
				code: '',
				segments,
				reason: `The ${LEVEL_NAMES[PU_LEVELS[i]]} part "${parts[i]}" should be ${PU_SEGMENT_WIDTHS[i]} digits or fewer.`,
			};
		}
		segments.push(padded);
	}

	return { ok: true, code: segments.join('-'), segments };
};

/** A complete, canonical polling-unit code — or exactly what is wrong with the input. */
export const normalizePuCode = (raw: unknown): IPuCodeParse => {
	const partial = parsePartialPuCode(raw, 4);
	if (!partial.ok) return partial;
	if (partial.segments.length < 4) {
		return {
			ok: false,
			code: '',
			segments: partial.segments,
			reason: 'A polling-unit code has 4 parts, like 04-09-02-011.',
		};
	}
	return partial;
};

/** The cumulative code for a level of a canonical PU code: `04`, `04-09`, `04-09-02`. */
export const prefixAt = (code: string, level: PuLevel): string => {
	const segments = code.split('-');
	return segments.slice(0, PU_LEVELS.indexOf(level) + 1).join('-');
};

/** The readout form: en-dash separated, `04 – 09 – 02 – 011`. Display only — never stored. */
export const displayPuCode = (code: string): string => code.split('-').join(' – ');
